import {useViewer} from '@github-ui/item-picker-assignee-relay/useViewer'
import type {AssigneePickerAssignee, fetchAssignees} from '@github-ui/item-picker-assignee-tsq'
import {AssigneeRepositoryPicker} from '@github-ui/item-picker-assignee-tsq'
import {formatActorLogin} from '@github-ui/item-picker-assignee-tsq/utils/assignee-picker'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {useCallback, useMemo, useState} from 'react'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {createQuickFilterAnchor, type QuickFilterProps} from './quick-filter-shared'
import {getRepoPullsFilterPickerTokens, replaceRepoPullsFilterInQuery} from './quick-filter-query'

const authorAnchor = createQuickFilterAnchor('Author')
const assigneeAnchor = createQuickFilterAnchor('Assignee')

type ActorQuickFilterProps = QuickFilterProps & {
  kind: 'assignee' | 'author'
}

function ActorQuickFilter({kind, owner, query, repo, onQueryChange, structuralUpdatesEnabled}: ActorQuickFilterProps) {
  const viewer = useViewer()
  const tokens = useMemo(
    () => getRepoPullsFilterPickerTokens(query, kind, structuralUpdatesEnabled).slice(-1),
    [kind, query, structuralUpdatesEnabled],
  )
  const isAuthor = kind === 'author'
  const anchor = isAuthor ? authorAnchor : assigneeAnchor
  // Store the initial author logins rather than a viewer-derived flag, because the viewer
  // may still be loading when the picker's one-time initial fetch resolves.
  const [initialAuthorLogins, setInitialAuthorLogins] = useState<ReadonlySet<string>>(() => new Set())
  // Only pin the viewer to the top of the author menu when they have authored a pull request.
  const viewerIsAuthor = !!viewer && initialAuthorLogins.has(viewer.login)
  const fetchAuthors = useCallback<typeof fetchAssignees>(
    async (queryClient, params) => {
      const response = await fetchAuthorSuggestions(queryClient, {
        ...params,
        loginNames: params.loginNames?.map(login => (login.toLowerCase() === '@me' && viewer ? viewer.login : login)),
      })
      if (!params.loginNames && !params.query) {
        setInitialAuthorLogins(new Set(response.assignees.map(author => author.login)))
      }
      return response
    },
    [viewer],
  )
  const onSelectionChange = useCallback(
    (actors: AssigneePickerAssignee[]) => {
      onQueryChange(
        replaceRepoPullsFilterInQuery(query, actors.map(formatActorLogin), kind, structuralUpdatesEnabled),
        kind,
      )
    },
    [kind, onQueryChange, query, structuralUpdatesEnabled],
  )

  return (
    <AssigneeRepositoryPicker
      name={kind}
      selectionVariant="single"
      readonly={false}
      title={`Filter by ${kind}`}
      assigneeTokens={tokens}
      assignees={[]}
      repo={repo}
      owner={owner}
      viewer={!isAuthor || viewerIsAuthor ? viewer : null}
      onSelectionChange={onSelectionChange}
      anchorElement={anchor}
      showNoMatchItem
      includeAuthorableBots={isAuthor}
      includeAssignableBots={!isAuthor}
      fetchAssignees={isAuthor ? fetchAuthors : undefined}
    />
  )
}

export function AuthorQuickFilter(props: QuickFilterProps) {
  return <ActorQuickFilter {...props} kind="author" />
}

export function AssigneeQuickFilter(props: QuickFilterProps) {
  return <ActorQuickFilter {...props} kind="assignee" />
}

type AuthorSuggestion = Pick<AssigneePickerAssignee, 'login' | 'name' | 'isCopilot'> & {avatarUrl?: string}

const fetchAuthorSuggestions: typeof fetchAssignees = (queryClient, {owner, repo, query, loginNames}) =>
  queryClient.fetchQuery({
    queryKey: ['pull-request-author-suggestions', owner, repo, query, loginNames],
    queryFn: async () => {
      const params = new URLSearchParams({
        repo: `${owner}/${repo}`,
        q: query,
        pull_request_authors: '1',
        include_bots: '1',
        show_author_copilot: '1',
      })
      let authors: AuthorSuggestion[]
      if (loginNames) {
        const validatedAuthors = await Promise.all(
          loginNames.map(async (login): Promise<AuthorSuggestion | null> => {
            const validationParams = new URLSearchParams(params)
            validationParams.set('q', login)
            const response = await reactFetchJSON(`/_filter/users/validate?${validationParams}`)
            return response.ok ? response.json() : null
          }),
        )
        authors = validatedAuthors.filter(author => author !== null)
      } else {
        const response = await reactFetchJSON(`/_filter/users?${params}`)
        const data: {users: AuthorSuggestion[]} = await response.json()
        authors = data.users
      }

      return {
        assignees: authors.map(author => ({
          ...author,
          avatarUrl: author.avatarUrl ?? '',
          id: author.login,
          login: author.login.replace(/^app\//, ''),
          // Match the GraphQL assignee shape so the picker renders Copilot's "GitHub" description.
          displayName: author.isCopilot ? LABELS.copilotDisplayName : undefined,
          __typename: author.login.startsWith('app/') || author.isCopilot ? 'Bot' : 'User',
        })),
      }
    },
  })
